import { useQuery } from "@tanstack/react-query";

import { api } from "@/api/client";
import type {
  CostByDayItem,
  CostByModelItem,
  CostByTeamItem,
  KnowledgeBaseSummary,
  LlmCatalogCostItem,
  McpSummary,
  MeUsage,
  ObservabilityFilters,
  ObservabilityStatus,
  ObservabilitySummary,
  ThreadMetrics,
  TopAgentItem,
  UsageByUserItem,
} from "@/api/types";

const KEY_STATUS = ["observability", "status"] as const;
const NS = "observability";

/**
 * Status do Langfuse. Frontend chama isso ANTES de renderizar qualquer
 * dashboard -- se enabled=false, mostra <DisabledBanner> em vez do dash.
 */
export function useObservabilityStatus() {
  return useQuery({
    queryKey: KEY_STATUS,
    queryFn: () => api.GET<ObservabilityStatus>("/observability/status"),
    // Status raramente muda -- staleTime alto.
    staleTime: 60_000,
  });
}

function _params(filters: ObservabilityFilters): Record<string, string | number> {
  const p: Record<string, string | number> = {};
  if (filters.from) p.from = filters.from;
  if (filters.to) p.to = filters.to;
  if (filters.team_id) p.team_id = filters.team_id;
  if (filters.agent_id) p.agent_id = filters.agent_id;
  if (filters.limit) p.limit = filters.limit;
  // Default do backend e false -- so envia quando ligado.
  if (filters.include_inactive) p.include_inactive = "true";
  return p;
}

export function useSummary(filters: ObservabilityFilters = {}) {
  return useQuery({
    queryKey: [NS, "summary", filters],
    queryFn: () =>
      api.GET<ObservabilitySummary>("/observability/summary", {
        params: _params(filters),
      }),
    staleTime: Infinity,
  });
}

export function useCostByDay(filters: ObservabilityFilters = {}) {
  return useQuery({
    queryKey: [NS, "cost-by-day", filters],
    queryFn: () =>
      api.GET<CostByDayItem[]>("/observability/cost-by-day", {
        params: _params(filters),
      }),
    staleTime: Infinity,
  });
}

export function useCostByModel(filters: ObservabilityFilters = {}) {
  return useQuery({
    queryKey: [NS, "cost-by-model", filters],
    queryFn: () =>
      api.GET<CostByModelItem[]>("/observability/cost-by-model", {
        params: _params(filters),
      }),
    staleTime: Infinity,
  });
}

export function useCostByTeam(filters: ObservabilityFilters = {}) {
  return useQuery({
    queryKey: [NS, "cost-by-team", filters],
    queryFn: () =>
      api.GET<CostByTeamItem[]>("/observability/cost-by-team", {
        params: _params(filters),
      }),
    staleTime: Infinity,
  });
}

export function useTopAgents(filters: ObservabilityFilters = {}) {
  return useQuery({
    queryKey: [NS, "top-agents", filters],
    queryFn: () =>
      api.GET<TopAgentItem[]>("/observability/top-agents", {
        params: _params(filters),
      }),
    staleTime: Infinity,
  });
}

export function useUsageByUser(filters: ObservabilityFilters = {}) {
  return useQuery({
    queryKey: [NS, "usage-by-user", filters],
    queryFn: () =>
      api.GET<UsageByUserItem[]>("/observability/usage-by-user", {
        params: _params(filters),
      }),
    staleTime: Infinity,
  });
}

export function useMeUsage(filters: ObservabilityFilters = {}) {
  return useQuery({
    queryKey: [NS, "me", filters],
    queryFn: () =>
      api.GET<MeUsage>("/observability/me", {
        params: _params(filters),
      }),
    staleTime: Infinity,
  });
}

export function useKbSummary() {
  return useQuery({
    queryKey: [NS, "kb-summary"],
    queryFn: () =>
      api.GET<KnowledgeBaseSummary>("/observability/kb-summary"),
    staleTime: 60_000,
  });
}

export function useMcpSummary() {
  return useQuery({
    queryKey: [NS, "mcp-summary"],
    queryFn: () => api.GET<McpSummary>("/observability/mcp-summary"),
    staleTime: 60_000,
  });
}

export function useLlmCatalogCosts(filters: ObservabilityFilters = {}) {
  return useQuery({
    queryKey: [NS, "llm-catalog-costs", filters],
    queryFn: () =>
      api.GET<LlmCatalogCostItem[]>("/observability/llm-catalog-costs", {
        params: _params(filters),
      }),
    staleTime: 60_000,
  });
}

/**
 * Metricas ao vivo de uma thread (custo/tokens/requests/modelos).
 * ``staleTime: Infinity`` -- chamada apenas no mount; refresh via
 * ``refetch()`` ao receber o evento ``done`` do SSE.
 */
export function useThreadMetrics(threadId: string | undefined) {
  return useQuery({
    queryKey: [NS, "thread-metrics", threadId],
    queryFn: () =>
      api.GET<ThreadMetrics>(
        `/observability/threads/${threadId}/metrics`,
      ),
    enabled: Boolean(threadId),
    staleTime: Infinity,
  });
}
